import type {
  BulkCandidate,
  BulkChannel,
  BulkRecipient,
  BulkSchedule,
  BulkUploadAnomaly,
} from '@/types/portal';
import { PLATFORM } from '@/config/platform';

export interface BulkTotals {
  /** Gross value of every row — what the batch would debit in total. */
  total: number;
  /** Early payout portion auto-swept to the Twiva Pool Account. */
  sweep: number;
  /** What actually reaches creators after the sweep. */
  toCreators: number;
  validCount: number;
  blockedCount: number;
}

/** Roll a recipient roster up into the numbers shown across the wizard. */
export function bulkTotals(rows: BulkRecipient[]): BulkTotals {
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  const sweep = rows.reduce((sum, row) => sum + (row.advance?.amount ?? 0), 0);
  const validCount = rows.filter((row) => row.status === 'Valid').length;
  return {
    total,
    sweep,
    toCreators: total - sweep,
    validCount,
    blockedCount: rows.length - validCount,
  };
}

export function channelLabel(channel: BulkChannel): string {
  return channel === 'wallet' ? `${PLATFORM.walletName} transfers` : 'Mobile Money';
}

export function channelDescription(channel: BulkChannel): string {
  return channel === 'wallet'
    ? `Wallet-to-Wallet transfers to recipients who hold a ${PLATFORM.payName} Wallet.`
    : `Send to M-Pesa or Airtel Money numbers for recipients without a ${PLATFORM.walletName}.`;
}

/** A candidate is available for the batch channel and the source wallet currency. */
export function candidateMatches(
  candidate: BulkCandidate,
  channel: BulkChannel,
  currency: string,
): boolean {
  if (candidate.currency !== currency) return false;
  if (channel === 'mobile') return candidate.walletType === 'Mobile';
  return candidate.walletType === 'Ledger' || candidate.walletType === 'Bank';
}

/** Fold a candidate into a roster row for the chosen channel. */
export function candidateToRecipient(
  candidate: BulkCandidate,
  channel: BulkChannel,
  description: string,
  source: BulkRecipient['source'],
): BulkRecipient {
  return {
    id: candidate.id,
    name: candidate.name,
    avatar: candidate.avatar,
    walletId: candidate.walletId,
    walletType: channel === 'mobile' ? 'Mobile' : candidate.walletType,
    network: channel === 'mobile' ? candidate.network : undefined,
    market: candidate.market,
    currency: candidate.currency,
    amount: candidate.suggestedAmount,
    description,
    campaign: candidate.campaign,
    source,
    advance: candidate.advance,
    status: candidate.blocked ? 'Blocked' : 'Valid',
    issue: candidate.blocked,
  };
}

/** Re-key a saved list's recipients so they never clash with the live roster. */
export function rehydrateList(listId: string, rows: BulkRecipient[]): BulkRecipient[] {
  return rows.map((row) => ({ ...row, id: `${listId}-${row.id}` }));
}

/** One flagged line in a parsed file, ready to show in the validation report. */
export interface BulkRowFlag {
  id: string;
  name: string;
  target: string;
  amount: number;
  reason: string;
}

/** Roll-up of a parsed roster shown before the user can reach Review. */
export interface BulkValidationSummary {
  total: number;
  ready: number;
  flagged: BulkRowFlag[];
}

/** Issues a user can correct directly inside the validation report. */
const FIXABLE_ISSUE_MESSAGES: readonly string[] = [
  'Missing wallet ID',
  'Missing mobile number',
  'Invalid amount — must be greater than zero',
];

/** Whether a flagged reason can be corrected inline in the validation report. */
export function isInlineFixableIssue(reason: string | null | undefined): boolean {
  return !!reason && FIXABLE_ISSUE_MESSAGES.includes(reason);
}

/** Where a flagged row's money would land, formatted for the report. */
function flagTarget(row: BulkRecipient): string {
  if (row.walletType === 'Mobile') {
    return `${row.network ?? 'Mobile Money'} · ${row.walletId || '\u2014'}`;
  }
  return `${row.walletType} wallet · ${row.walletId || '\u2014'}`;
}

/** Why a parsed row cannot be paid — null when the row is usable. */
function recipientRowIssue(row: BulkRecipient, channel: BulkChannel): string | null {
  if (!row.walletId || !row.walletId.trim()) {
    return channel === 'mobile' ? 'Missing mobile number' : 'Missing wallet ID';
  }
  if (!row.amount || row.amount <= 0 || Number.isNaN(row.amount)) {
    return 'Invalid amount — must be greater than zero';
  }
  // Hard blocks (e.g. an incomplete KYC) are not correctable from the report.
  if (row.issue && !isInlineFixableIssue(row.issue)) return row.issue;
  return null;
}

/** Roll a roster into the pre-review report without rewriting any row. */
export function summarizeRows(
  rows: BulkRecipient[],
  channel: BulkChannel,
): BulkValidationSummary {
  const flagged: BulkRowFlag[] = [];
  let ready = 0;

  rows.forEach((row) => {
    const reason = recipientRowIssue(row, channel);
    if (reason) {
      flagged.push({ id: row.id, name: row.name, target: flagTarget(row), amount: row.amount, reason });
    } else {
      ready += 1;
    }
  });

  return { total: rows.length, ready, flagged };
}

/** Re-status a roster and roll its problems into the pre-review validation report. */
export function applyRowValidation(
  rows: BulkRecipient[],
  channel: BulkChannel,
): { rows: BulkRecipient[]; summary: BulkValidationSummary } {
  const summary = summarizeRows(rows, channel);
  const reasonById = new Map(summary.flagged.map((flag) => [flag.id, flag.reason]));

  const validated: BulkRecipient[] = rows.map((row) => {
    const reason = reasonById.get(row.id);
    if (reason) {
      return { ...row, status: 'Blocked' as const, issue: reason };
    }
    return { ...row, status: 'Valid' as const, issue: undefined };
  });

  return { rows: validated, summary };
}

/** Fold a messy uploaded line into a roster row so the report can surface it. */
export function anomalyToRecipient(
  anomaly: BulkUploadAnomaly,
  channel: BulkChannel,
  currency: string,
  description: string,
  index: number,
): BulkRecipient {
  return {
    id: `upload-anomaly-${index}`,
    name: anomaly.name,
    walletId: anomaly.walletId,
    walletType: channel === 'mobile' ? 'Mobile' : 'Ledger',
    network: channel === 'mobile' ? 'M-Pesa' : undefined,
    market: currency === 'TZS' ? 'Tanzania' : currency === 'UGX' ? 'Uganda' : 'Kenya',
    currency,
    amount: anomaly.amount,
    description,
    source: 'file',
    status: 'Blocked',
  };
}

/** Today as an ISO date (yyyy-mm-dd), used as the minimum selectable schedule day. */
export function todayIso(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
}

/** A fresh schedule — defaults to "pay now" with tomorrow 09:00 pre-filled. */
export function defaultBulkSchedule(): BulkSchedule {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return { mode: 'now', date: tomorrow.toISOString().slice(0, 10), time: '09:00' };
}

/** Turn a schedule into a Date, or null when the parts aren't filled in. */
export function scheduleToDate(schedule: BulkSchedule): Date | null {
  if (!schedule.date) return null;
  const date = new Date(`${schedule.date}T${schedule.time || '00:00'}`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Human-readable release time, e.g. "17 Sep 2026, 09:00". */
export function formatScheduleWhen(schedule: BulkSchedule): string {
  const date = scheduleToDate(schedule);
  if (!date) return '';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A scheduled batch must be fully filled in and in the future. */
export function isScheduleValid(schedule: BulkSchedule): boolean {
  if (schedule.mode === 'now') return true;
  const date = scheduleToDate(schedule);
  return date !== null && date.getTime() > Date.now();
}

/** One-line label for a schedule, used on review, confirm and receipts. */
export function scheduleLabel(schedule: BulkSchedule): string {
  if (schedule.mode === 'now') return 'Send immediately';
  const when = formatScheduleWhen(schedule);
  return when ? `Scheduled for ${when}` : 'Choose a date and time';
}

const MONTHS_SHORT = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

/**
 * Turn a stored "18 Sep 2026, 09:00" label back into an editable schedule so a
 * scheduled batch can be rescheduled. Falls back to tomorrow 09:00 if unreadable.
 */
export function scheduleFromLabel(label?: string): BulkSchedule {
  const fallback = defaultBulkSchedule();
  if (!label) return { ...fallback, mode: 'scheduled' };
  const match = label.match(/(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4}),?\s+(\d{2}):(\d{2})/);
  if (!match) return { ...fallback, mode: 'scheduled' };
  const month = MONTHS_SHORT.indexOf(match[2].toLowerCase());
  if (month < 0) return { ...fallback, mode: 'scheduled' };
  const date = `${match[3]}-${String(month + 1).padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  return { mode: 'scheduled', date, time: `${match[4]}:${match[5]}` };
}